import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { adminApi, unwrap } from '../services/api'
import { istDate } from '../utils/dateIST'
import { useColors } from '../hooks/useColors'
import EmptyState from '../components/shared/EmptyState'
import ErrorState from '../components/shared/ErrorState'

interface ExpenseRecord {
  id: string
  category: string
  description: string
  amount: number
  date: string
  recurring?: boolean
  recurring_frequency?: string | null
  created_at?: string
  created_by?: string | null
  created_by_name?: string | null
}

interface ExpensesPayload {
  expenses?: ExpenseRecord[]
  page?: number
  limit?: number
  total?: number
  summary?: {
    total_expenses?: number
    by_category?: Record<string, number>
  }
}

interface ProfitSummary {
  revenue?: number
  expenses?: number
  profit?: number
  margin_percent?: number
  revenue_by_day?: Array<{ date: string | null; revenue: number }>
  expenses_by_category?: Record<string, number>
}

const CATEGORIES = [
  { value: 'rent', label: 'Rent' },
  { value: 'salary', label: 'Salary' },
  { value: 'transport', label: 'Transport' },
  { value: 'materials', label: 'Materials' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'other', label: 'Other' },
]

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(value || 0))

const toMonthKey = (value: string) => value.slice(0, 7)

const buildMonthList = (start: Date, end: Date) => {
  const months: string[] = []
  const cursor = new Date(start)
  cursor.setDate(1)

  while (cursor <= end && months.length < 12) {
    months.push(cursor.toISOString().slice(0, 7))
    cursor.setMonth(cursor.getMonth() + 1)
  }

  return months
}

const formatMonthLabel = (value: string) => {
  const [year, month] = value.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short' })
}

const CARD_SHADOW = '0px 2px 8px rgba(0,0,0,0.04)'

export default function ExpensesPage() {
  const colors = useColors()
  const PIE_COLORS = ['#D6B97B', colors.textPrimary, colors.textMuted, '#F59E0B', '#1D4ED8', '#7C3AED']
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [category, setCategory] = useState('')

  const [expenses, setExpenses] = useState<ExpenseRecord[]>([])
  const [summary, setSummary] = useState<ProfitSummary | null>(null)
  const [expenseSummary, setExpenseSummary] = useState<ExpensesPayload['summary'] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [formCategory, setFormCategory] = useState('rent')
  const [formDescription, setFormDescription] = useState('')
  const [formAmount, setFormAmount] = useState('')
  const [formDate, setFormDate] = useState(istDate())
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const params = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        category: category || undefined,
        limit: 200,
      }

      const [expenseRes, profitRes] = await Promise.allSettled([
        adminApi.getExpenses(params),
        adminApi.getProfitSummary({ date_from: dateFrom || undefined, date_to: dateTo || undefined }),
      ])

      if (expenseRes.status === 'fulfilled') {
        const payload = unwrap<ExpensesPayload>(expenseRes.value)
        setExpenses(Array.isArray(payload?.expenses) ? payload.expenses : [])
        setExpenseSummary(payload?.summary || null)
      }

      if (profitRes.status === 'fulfilled') {
        const payload = unwrap<ProfitSummary>(profitRes.value)
        setSummary(payload || null)
      }
    } catch {
      setError('Unable to load expenses. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [category, dateFrom, dateTo])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const resetForm = () => {
    setFormCategory('rent')
    setFormDescription('')
    setFormAmount('')
    setFormDate(istDate())
    setFormError('')
  }

  const handleCreateExpense = async () => {
    if (!formDescription || !formAmount || !formDate) {
      setFormError('Please fill all required fields.')
      return
    }
    const amount = Number(formAmount)
    if (!Number.isFinite(amount) || amount <= 0) {
      setFormError('Amount must be a positive number.')
      return
    }

    setSaving(true)
    setFormError('')

    try {
      await adminApi.createExpense({
        category: formCategory,
        description: formDescription,
        amount,
        date: formDate,
      })
      setShowModal(false)
      resetForm()
      await loadData()
    } catch (err) {
      setFormError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          || 'Unable to save expense. Please try again.'
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteExpense = async (id: string) => {
    if (!window.confirm('Delete this expense?')) {
      return
    }

    try {
      await adminApi.deleteExpense(id)
      await loadData()
    } catch {
      setError('Unable to delete expense. Please try again.')
    }
  }

  const chartData = useMemo(() => {
    const now = new Date()
    const start = dateFrom ? new Date(dateFrom) : new Date(now.getFullYear(), now.getMonth() - 5, 1)
    const end = dateTo ? new Date(dateTo) : now

    const monthKeys = buildMonthList(start, end)
    const revenueMap: Record<string, number> = {}
    const expenseMap: Record<string, number> = {}

    summary?.revenue_by_day?.forEach((row) => {
      if (!row.date) return
      const monthKey = toMonthKey(row.date)
      revenueMap[monthKey] = (revenueMap[monthKey] || 0) + Number(row.revenue || 0)
    })

    expenses.forEach((expense) => {
      const monthKey = toMonthKey(expense.date)
      expenseMap[monthKey] = (expenseMap[monthKey] || 0) + Number(expense.amount || 0)
    })

    return monthKeys.map((month) => ({
      month: formatMonthLabel(month),
      revenue: Number(revenueMap[month] || 0),
      expenses: Number(expenseMap[month] || 0),
    }))
  }, [dateFrom, dateTo, expenses, summary?.revenue_by_day])

  const pieData = useMemo(() => {
    const source = summary?.expenses_by_category || expenseSummary?.by_category || {}
    return Object.entries(source).map(([key, value]) => ({
      name: key,
      value: Number(value || 0),
    }))
  }, [expenseSummary?.by_category, summary?.expenses_by_category])

  const revenue = Number(summary?.revenue || 0)
  const totalExpenses = Number(summary?.expenses || expenseSummary?.total_expenses || 0)
  const profit = Number(summary?.profit || revenue - totalExpenses)
  const margin = Number(summary?.margin_percent || (revenue ? (profit / revenue) * 100 : 0))

  if (loading) {
    return (
      <div>
        <div className="rapidry-pulse" style={{ height: 160, borderRadius: '16px', background: colors.surface }} />
      </div>
    )
  }

  return (
    <div>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
        <div>
          <h1 className="font-display" style={{ margin: 0, fontSize: '26px', color: colors.textPrimary }}>
            Expenses
          </h1>
          <p className="font-body" style={{ margin: '6px 0 0', color: colors.textMuted }}>
            Track business spending and see real profit.
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true) }}
          style={{
            border: 'none',
            borderRadius: '999px',
            background: colors.textPrimary,
            color: colors.surface,
            padding: '10px 18px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          + Add Expense
        </button>
      </header>

      {error ? <ErrorState message={error} onRetry={loadData} /> : null}

      <section style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '22px' }}>
        {[
          { label: 'Total Revenue', value: revenue, color: colors.textPrimary },
          { label: 'Total Expenses', value: totalExpenses, color: '#B45309' },
          { label: 'Net Profit', value: profit, color: profit >= 0 ? '#15803D' : '#991B1B' },
          { label: 'Profit Margin %', value: margin.toFixed(1), color: '#1D4ED8', suffix: '%' },
        ].map((card) => (
          <div key={card.label} style={{ flex: 1, minWidth: '180px', background: colors.bgCard, borderRadius: '16px', padding: '18px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{card.label}</div>
            <div style={{ fontSize: '26px', fontWeight: 700, color: card.color, marginTop: '8px' }}>
              {card.suffix ? `${card.value}${card.suffix}` : `₹${formatCurrency(Number(card.value))}`}
            </div>
          </div>
        ))}
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '18px', marginBottom: '24px' }}>
        <div style={{ background: colors.bgCard, borderRadius: '18px', padding: '20px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
          <h3 className="font-body" style={{ margin: '0 0 12px', color: colors.textPrimary }}>
            Revenue vs Expenses (Monthly)
          </h3>
          <div style={{ width: '100%', height: '280px' }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.border} />
                <XAxis dataKey="month" tick={{ fill: colors.textMuted, fontSize: 12 }} />
                <YAxis tick={{ fill: colors.textMuted, fontSize: 12 }} />
                <Tooltip formatter={(value) => `₹${formatCurrency(Number(value))}`} />
                <Bar dataKey="revenue" fill={colors.textPrimary} radius={[8, 8, 0, 0]} />
                <Bar dataKey="expenses" fill="#D6B97B" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div style={{ background: colors.bgCard, borderRadius: '18px', padding: '20px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
          <h3 className="font-body" style={{ margin: '0 0 12px', color: colors.textPrimary }}>Expense Mix</h3>
          <div style={{ width: '100%', height: '280px' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={4}>
                  {pieData.map((entry, index) => (
                    <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `₹${formatCurrency(Number(value))}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      <section style={{ background: colors.bgCard, borderRadius: '18px', padding: '20px', boxShadow: CARD_SHADOW, border: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
          <div>
            <label className="font-body" style={{ display: 'block', fontSize: '11px', color: colors.textMuted }}>From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '8px 10px', borderRadius: '10px', border: `1px solid ${colors.border}` }} />
          </div>
          <div>
            <label className="font-body" style={{ display: 'block', fontSize: '11px', color: colors.textMuted }}>To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '8px 10px', borderRadius: '10px', border: `1px solid ${colors.border}` }} />
          </div>
          <div>
            <label className="font-body" style={{ display: 'block', fontSize: '11px', color: colors.textMuted }}>Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ padding: '8px 10px', borderRadius: '10px', border: `1px solid ${colors.border}`, minWidth: '160px' }}>
              <option value="">All</option>
              {CATEGORIES.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </select>
          </div>
          <button onClick={loadData} style={{ marginTop: '18px', borderRadius: '999px', border: `1px solid ${colors.textPrimary}`, padding: '8px 16px', background: colors.bgCard, color: colors.textPrimary, fontWeight: 600 }}>
            Apply Filters
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Date', 'Category', 'Description', 'Amount', 'Created By', ''].map((label) => (
                  <th key={label} style={{ textAlign: 'left', padding: '12px 14px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `1px solid ${colors.border}`, background: colors.bgSecondary }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="No expenses found" description="Try adjusting your filters." />
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td style={{ padding: '14px', borderBottom: `1px solid ${colors.surface}` }}>{expense.date}</td>
                    <td style={{ padding: '14px', borderBottom: `1px solid ${colors.surface}`, textTransform: 'capitalize' }}>{expense.category}</td>
                    <td style={{ padding: '14px', borderBottom: `1px solid ${colors.surface}` }}>{expense.description}</td>
                    <td style={{ padding: '14px', borderBottom: `1px solid ${colors.surface}`, fontWeight: 600 }}>₹{formatCurrency(expense.amount)}</td>
                    <td style={{ padding: '14px', borderBottom: `1px solid ${colors.surface}` }}>{expense.created_by_name || 'Admin'}</td>
                    <td style={{ padding: '14px', borderBottom: `1px solid ${colors.surface}` }}>
                      <button
                        onClick={() => handleDeleteExpense(expense.id)}
                        style={{ border: 'none', background: 'transparent', color: 'var(--color-error)', fontWeight: 600, cursor: 'pointer' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,46,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ width: '420px', background: colors.bgCard, borderRadius: '18px', padding: '22px', boxShadow: CARD_SHADOW }}>
            <h3 className="font-body" style={{ marginTop: 0, color: colors.textPrimary }}>Add Expense</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>Category</label>
                <select value={formCategory} onChange={(e) => setFormCategory(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `1px solid ${colors.border}` }}>
                  {CATEGORIES.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>Description</label>
                <input value={formDescription} onChange={(e) => setFormDescription(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `1px solid ${colors.border}` }} />
              </div>
              <div>
                <label className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>Amount</label>
                <input type="number" min="0" step="0.01" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `1px solid ${colors.border}` }} />
              </div>
              <div>
                <label className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>Date</label>
                <input type="date" value={formDate} onChange={(e) => setFormDate(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: `1px solid ${colors.border}` }} />
              </div>
              {formError ? (
                <p className="font-body" style={{ margin: 0, fontSize: '13px', color: 'var(--color-error)' }}>{formError}</p>
              ) : null}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button onClick={() => { setShowModal(false); resetForm() }} style={{ borderRadius: '999px', border: `1px solid ${colors.border}`, padding: '8px 14px', background: colors.bgCard, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleCreateExpense} disabled={saving} style={{ borderRadius: '999px', border: 'none', padding: '8px 16px', background: colors.textPrimary, color: colors.surface, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : 'Save Expense'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
